import { AlertTriangleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatZonedDateTime } from '../../lib/date-formatting';
import type { EffectiveDate } from './effective-date';
import { useTimeZone } from '@folio/utils/time-zone';
import { Text } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

type Consequence = 'cover' | 'billing' | 'access' | 'notification';

const CONSEQUENCES: Consequence[] = ['cover', 'billing', 'access', 'notification'];

export function ConsequencesPanel({
  effectiveDate,
  productId,
}: {
  effectiveDate: EffectiveDate;
  productId: string;
}) {
  const { t } = useTranslation();
  const timeZone = useTimeZone();
  const variant = effectiveDate.kind === 'VOIDED_AT_START' ? 'void' : 'scheduled';
  const date = effectiveDate.at
    ? formatZonedDateTime(new Date(effectiveDate.at), timeZone)
    : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangleIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
          {t('stopFunnel.consequences.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Stack asChild gap={1}>
          <ul>
            {CONSEQUENCES.map(consequence => (
              <li key={consequence} className="text-sm">
                <Text inline weight="medium">
                  {t(`stopFunnel.consequences.${consequence}.label`)}
                </Text>{' '}
                {t(
                  [
                    `${productId}.stopFunnel.consequences.${consequence}.${variant}`,
                    `stopFunnel.consequences.${consequence}.${variant}`,
                  ],
                  { date },
                )}
              </li>
            ))}
          </ul>
        </Stack>
      </CardContent>
    </Card>
  );
}
